// src/ui/steps/step4-festival-zzp.jsx — Festival ZZP-flow eindscherm
// Toont openingsbod = werkdagen × dagtarief (= uurtarief × 7,6). Festivaldagen zijn
// informatief (onderhandelpunt SAV, niet in het bedrag).
import React from "react";
import { FW, FwButton, FwCard, FwInlineBanner, StepHeader, ResultaatSamenvatting } from "../primitives";
import { calc } from "../../lib/calc";
import { aiHelp } from "../../lib/ai";
import { config } from "../../lib/config";
import { Aanname, Regel, OpslagTabel, RolOmschrijvingDetail } from "./step4-result";
import { exportFestivalZzp } from "../../lib/docx-export";

export const StepFestivalZzpResult = ({ state, setState, back, restart }) => {
  const huidigeRol = state.rollen?.[0];
  const result = huidigeRol ? calc.berekenFestivalZzp(huidigeRol) : null;

  const [copied, setCopied] = React.useState(false);
  const [sharing, setSharing] = React.useState(false);
  const [signalen, setSignalen] = React.useState([]);
  const [loadingSig, setLoadingSig] = React.useState(false);
  const [sigError, setSigError] = React.useState(false);

  React.useEffect(() => {
    if (!huidigeRol || !state.omschrijving) return;
    const ctrl = new AbortController();
    setLoadingSig(true);
    aiHelp.signalOnderhandelpunten(
      state.omschrijving,
      { signal: ctrl.signal },
      {
        rol: huidigeRol.rol,
        niveau: huidigeRol.niveau,
        werkdagen: huidigeRol.werkdagen || {},
      },
    ).then(r => {
      if (ctrl.signal.aborted) return;
      setLoadingSig(false);
      if (r?.signalen) setSignalen(r.signalen);
      else setSigError(true);
    });
    return () => ctrl.abort();
  }, []);

  if (!huidigeRol || !result) {
    return <div style={{ padding: 40, color: FW.navy }}>Geen berekening mogelijk — ga terug en vul het aantal werkdagen in.</div>;
  }

  const btwPct = Number(config.generiek?.btw_pct) || 0.21;
  const totaalExBtw = result.openingsbod;
  const btw = totaalExBtw * btwPct;
  const totaalInclBtw = totaalExBtw + btw;

  const shareLink = () => {
    const json = btoa(unescape(encodeURIComponent(JSON.stringify(state))));
    const url = `${location.origin}${location.pathname}#berekening=${json}`;
    navigator.clipboard.writeText(url);
    setSharing(true);
    setTimeout(() => setSharing(false), 2000);
  };
  const copyEmail = () => {
    const txt = generateEmailFestivalZzp(state, huidigeRol, result, totaalInclBtw);
    navigator.clipboard.writeText(txt);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const downloadWord = () => exportFestivalZzp({ state, huidigeRol, result, totaalExBtw, btw, totaalInclBtw, signalen });

  return (
    <div className="result-page">
      <StepHeader
        tag="Openingsbod"
        title="Jouw minimum dagtarief"
        sub="Op basis van de Fair Pay richtlijn 2026 voor filmfestivals en jouw werkdagen. Eén werkdag = 7,6 uur (excl. pauze, conform SAV)."
        onBack={back}
      />

      <ResultaatSamenvatting
        isFestival={true}
        vorm={state.vorm}
        rol={huidigeRol.rol}
        dep={huidigeRol.dep}
        niveau={huidigeRol.niveau}
      />

      <div className="row-2col" style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr", gap: 24, alignItems: "start" }}>
        <div>
          <div style={{ background: FW.white, border: `1.5px solid ${FW.gray100}`, borderRadius: 20, padding: 24 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: "8px 0", fontSize: 15, color: FW.navy }}>
              <span>{result.werkdagenTotaal} werkdagen × {calc.fmt(result.dagtarief)}</span>
              <span style={{ fontWeight: 700 }}>{calc.fmt(result.openingsbod)}</span>
            </div>
            <div style={{ height: 1, background: FW.gray100, margin: "12px 0" }} />
            <Regel label="Totaal excl. btw" bedrag={totaalExBtw} strong />
            <Regel label="21% btw" bedrag={btw} />
            <Regel label="Totaal incl. btw" bedrag={totaalInclBtw} strong />
          </div>

          <div style={{ marginTop: 32, fontSize: 20, fontWeight: 700, marginBottom: 6 }}>Aannames</div>
          <div style={{ fontSize: 14, color: FW.navy2, marginBottom: 16 }}>
            Klik op een aanname om de onderbouwing te zien of de waarde aan te passen.
          </div>
          <div style={{ display: "grid", gap: 8 }}>
            {state.omschrijving && (
              <Aanname
                label="Omschrijving"
                waarde={state.omschrijving.length > 80 ? state.omschrijving.slice(0, 80).trim() + "…" : state.omschrijving}
                uitleg="Dit is de tekst die je hebt ingevuld als beschrijving van de opdracht. AI gebruikt deze tekst om rol, schaal en werkdagen te suggereren."
                onEdit={() => { back(); back(); back(); }}
                detail={
                  <div style={{ background: FW.white, border: `1px solid ${FW.gray100}`, borderRadius: 10, padding: 12, fontSize: 14, color: FW.navy2, lineHeight: 1.55, whiteSpace: "pre-wrap", marginBottom: 12 }}>
                    {state.omschrijving}
                  </div>
                }
              />
            )}
            <Aanname
              label="Type opdracht"
              waarde="Filmfestival — zzp"
              uitleg="Voor filmfestivals geldt een eigen Fair Pay richtlijn met 12 schalen (1-12, Cao Toneel & Dans). Voor zzp'ers wordt het zzp-starttarief gebruikt, met een opslagfactor van 69,26%."
              onEdit={() => restart()}
            />
            <Aanname
              label="Rol / functie"
              waarde={`${huidigeRol.rol} · ${huidigeRol.dep}`}
              uitleg="Op basis van jouw omschrijving (of jouw keuze in de rollen-lijst) is deze rol gekozen uit de Matrix Functies Filmfestivals."
              onEdit={() => { back(); back(); back(); }}
              detail={<RolOmschrijvingDetail rol={huidigeRol.rol} dep={huidigeRol.dep} isFestival={true} />}
            />
            <Aanname
              label="Zwaarteniveau"
              waarde={`Schaal ${huidigeRol.niveau}${result.tariefRij?.rol_voorbeeld ? ` (${result.tariefRij.rol_voorbeeld})` : ""}`}
              uitleg={`Schaal ${huidigeRol.niveau} (CAO Toneel & Dans) geeft het minimum zzp-uurtarief van ${calc.fmt(result.uurtarief)}/u, oftewel ${calc.fmt(result.dagtarief)} per dag (× 7,6 u).`}
              onEdit={() => { back(); back(); }}
            />
            <Aanname
              label="Opslagfactor"
              waarde="69,26% (digiPACCT)"
              uitleg="Voor filmfestivals geldt de digiPACCT-methode: bedrijfskosten (15%), niet-declarabele uren (17%), vakantietoeslag (8%), ziekte/arbeidsongeschiktheid (14,5%), ZVW (5,32%) en pensioen (9,44%). Samen: 69,26%."
              detail={<OpslagTabel isFestival={true} />}
            />
            <Aanname
              label="Werkdagen"
              waarde={`${result.werkdagenTotaal} dagen`}
              uitleg="Totaal aantal dagen dat je aan deze opdracht besteedt — voorbereiding, festival én afronding samen. Eén werkdag = 7,6 uur (excl. pauze, conform SAV)."
              onEdit={() => back()}
            />
            {result.festivaldagen > 0 && (
              <Aanname
                label="Waarvan festivaldagen"
                waarde={`${result.festivaldagen} dagen`}
                uitleg="Dagen waarop het festival zelf plaatsvindt. Tellen niet apart in het openingsbod, maar zijn een aanknopingspunt voor onderhandeling over festivaldag-toeslag, lunch/diner en reisuren."
                onEdit={() => back()}
              />
            )}
          </div>
        </div>

        <div className="aside-card" style={{ display: "grid", gap: 16, position: "sticky", top: 20 }}>
          <FwCard padded={false} style={{ padding: 22, background: FW.cream }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: FW.navy, marginBottom: 10 }}>Goed om te benoemen</div>
            <ul style={{ fontSize: 14, color: FW.navy2, margin: 0, paddingLeft: 16, lineHeight: 1.55, display: "grid", gap: 8 }}>
              <li>Dit is een <b>minimum</b> dagtarief volgens Fair Pay. Meer ervaring, complexiteit en SAV-toeslagen zijn <b>onderhandelruimte</b>.</li>
              <li>Reis-, materiaal- en inhuurkosten mag je <b>apart factureren</b> bovenop het dagtarief.</li>
              <li>Het tarief bevat al een <b>opslag van 69,26%</b> voor zzp-lasten, niet-declarabele tijd, vakantie, ziekte en pensioen.</li>
            </ul>
          </FwCard>

          {loadingSig && (
            <div style={{ padding: 16, background: FW.beige2, borderRadius: 14, fontSize: 14, color: FW.navy, display: "inline-flex", alignItems: "center", gap: 10 }}>
              <i className="ri-sparkling-2-line" />
              Aandachtspunten voor je onderhandeling worden geladen…
            </div>
          )}
          {!loadingSig && sigError && (
            <FwInlineBanner type="warning" onDismiss={() => setSigError(false)}>
              <div style={{ fontWeight: 700 }}>Onderhandelpunten konden niet geladen worden.</div>
            </FwInlineBanner>
          )}
          {!loadingSig && (
            <FwCard padded={false} style={{ padding: 22, background: FW.cream, border: `1.5px solid ${FW.yellow}` }}>
              <h3 style={{ margin: "0 0 6px", fontSize: 14, fontWeight: 700, color: FW.navy, display: "inline-flex", alignItems: "center", gap: 6 }}>
                <i className="ri-chat-3-line" /> Aandachtspunten voor je onderhandeling
              </h3>
              <p style={{ fontSize: 13, color: FW.navy2, margin: "0 0 8px", lineHeight: 1.45 }}>
                Dit dagtarief is het minimum volgens Fair Pay. De Standaard Basisregeling SAV Filmfestivals geeft je extra grond voor:
              </p>
              <ul style={{ margin: "0 0 12px", paddingLeft: 16, display: "grid", gap: 4 }}>
                <li style={{ fontSize: 13, color: FW.navy2, lineHeight: 1.45 }}><b>Festivaldag-toeslag</b> — een opslag op dagen tijdens het festival zelf.</li>
                <li style={{ fontSize: 13, color: FW.navy2, lineHeight: 1.45 }}><b>Nachtwerk</b> — 25% toeslag op uren tussen 22:00 en 06:00.</li>
                <li style={{ fontSize: 13, color: FW.navy2, lineHeight: 1.45 }}><b>Reisuren</b> — gelden als arbeidstijd en worden volledig vergoed.</li>
                <li style={{ fontSize: 13, color: FW.navy2, lineHeight: 1.45 }}><b>Lunch en diner</b> — tijdens het festival door het festival aangeboden.</li>
                <li style={{ fontSize: 13, color: FW.navy2, lineHeight: 1.45 }}>Reis- en verblijfkosten, materiaal en inhuur kun je <b>apart factureren</b> bovenop het dagtarief.</li>
              </ul>
              {signalen.length > 0 && (
                <>
                  <p style={{ fontSize: 13, fontWeight: 700, color: FW.navy, margin: "0 0 6px", lineHeight: 1.45 }}>
                    Specifiek uit jouw omschrijving:
                  </p>
                  <ul style={{ margin: 0, paddingLeft: 16, display: "grid", gap: 6 }}>
                    {signalen.map((s, i) => (
                      <li key={i} style={{ fontSize: 13, color: FW.navy2, lineHeight: 1.45 }}>
                        <strong style={{ color: FW.navy }}>{s.factor}</strong> — {s.toelichting}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </FwCard>
          )}
        </div>
      </div>

      <FwCard tint="navy" padded={false} className="summary-card" style={{ padding: "40px 44px", marginTop: 32 }}>
        <div className="summary-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 32, alignItems: "end" }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: FW.yellow, opacity: .9 }}>Dagtarief</div>
            <div className="summary-value" style={{ fontSize: 48, fontWeight: 700, color: "#fff", lineHeight: 1, marginTop: 8, fontVariantNumeric: "tabular-nums" }}>
              {calc.fmt(result.dagtarief)}
            </div>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,.65)", marginTop: 6 }}>
              Uurtarief {calc.fmt(result.uurtarief)} × 7,6 u · incl. 69,26% opslag
            </div>
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: FW.yellow, opacity: .9 }}>Werkdagen</div>
            <div className="summary-value" style={{ fontSize: 48, fontWeight: 700, color: "#fff", lineHeight: 1, marginTop: 8, fontVariantNumeric: "tabular-nums" }}>
              {result.werkdagenTotaal}
            </div>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,.65)", marginTop: 6 }}>
              {result.festivaldagen > 0 ? `Waarvan ${result.festivaldagen} festivaldag${result.festivaldagen === 1 ? "" : "en"}` : "Geen festivaldagen apart opgegeven"}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: FW.yellow, opacity: .9 }}>Openingsbod</div>
            <div className="summary-value" style={{ fontSize: 48, fontWeight: 700, color: "#fff", lineHeight: 1, marginTop: 8, fontVariantNumeric: "tabular-nums" }}>
              {calc.fmtInt(result.openingsbod)}
            </div>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,.65)", marginTop: 6 }}>
              Excl. btw · incl: {calc.fmtInt(totaalInclBtw)}
            </div>
          </div>
        </div>
      </FwCard>

      <FwCard padded={false} style={{ marginTop: 24, padding: 22, background: FW.mint }} className="hide-print">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
          <div style={{ minWidth: 0, flex: "1 1 260px" }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: FW.navy, marginBottom: 4, display: "inline-flex", alignItems: "center", gap: 6 }}>
              <i className="ri-send-plane-line" /> Deel je openingsbod
            </div>
            <div style={{ fontSize: 14, color: FW.navy2, lineHeight: 1.4 }}>
              Deel via een link, kopieer een e-mailtekst, of download als onderbouwingsdocument.
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <FwButton variant="navy" icon="ri-link" arrow={false} onClick={shareLink}>
              {sharing ? "Link gekopieerd ✓" : "Kopieer deelbare link"}
            </FwButton>
            <FwButton variant="secondary" icon="ri-mail-line" arrow={false} onClick={copyEmail}>
              {copied ? "E-mailtekst gekopieerd ✓" : "Kopieer e-mailtekst"}
            </FwButton>
            <FwButton variant="secondary" icon="ri-file-word-2-line" arrow={false} onClick={downloadWord}>
              Download als Word
            </FwButton>
          </div>
        </div>
      </FwCard>

      <div style={{ marginTop: 32, display: "flex", justifyContent: "space-between" }} className="hide-print step-nav">
        <FwButton variant="ghost" arrow={false} onClick={back}>← Pas werkdagen aan</FwButton>
        <FwButton variant="secondary" arrow={false} onClick={restart}>Nieuwe berekening</FwButton>
      </div>
    </div>
  );
};

function generateEmailFestivalZzp(state, rol, result, totaalInclBtw) {
  const fdRegel = result.festivaldagen > 0 ? `, waarvan ${result.festivaldagen} festivaldag${result.festivaldagen === 1 ? "" : "en"}` : "";
  return `Beste [opdrachtgever],

Hierbij een openingsbod voor mijn inzet als ${rol.rol} (${rol.dep}) op jouw filmfestival.

Op basis van de Fair Pay richtlijn 2026 voor filmfestivals (schaal ${rol.niveau} volgens CAO Toneel & Dans):

• Uurtarief: ${calc.fmt(result.uurtarief)} (incl. 69,26% opslag)
• Dagtarief: ${calc.fmt(result.dagtarief)} (uurtarief × 7,6 u, conform SAV)
• Werkdagen: ${result.werkdagenTotaal}${fdRegel}
• Openingsbod: ${calc.fmtInt(result.openingsbod)} excl. btw (${calc.fmtInt(totaalInclBtw)} incl. btw)

Dit is een minimum dagtarief conform de Fair Pay richtlijn van de sector. Conform de Standaard Basisregeling SAV Filmfestivals ga ik graag in gesprek over festivaldag-toeslag, nachtwerk-toeslag, vergoeding van reisuren als arbeidstijd, en lunch/diner tijdens het festival.

Met vriendelijke groet,
[jouw naam]`;
}
